import { useId, type ComponentPropsWithRef, type ReactNode, type Ref } from 'react'

import { t } from '@ctcells/i18n'

import { useDisabled } from './disabled.tsx'
import { Icon } from './Icon.tsx'
import './overlay.css'
import './dialogSurface.css'

export type DialogSurfaceProps = Omit<ComponentPropsWithRef<'div'>, 'title'> & {
  title: string
  titleMark?: ReactNode | undefined
  /**
   * 제목 «오른쪽» 같은 줄의 보조 표기(예: 웹훅 추가 창의 「(아이콘) Slack」 · eric 2026-10-30).
   * 🔴 제목(`h2`) 밖에 선다 — 창의 접근 이름은 `title` 글자 그대로다.
   */
  titleAside?: ReactNode | undefined
  titleId?: string | undefined
  description?: string | undefined
  descriptionId?: string | undefined
  onClose: () => void
  busy?: boolean | undefined
  footer?: ReactNode | undefined
  footerRef?: Ref<HTMLDivElement> | undefined
}

/**
 * 외관만 공유한다. 위치·dim·포커스·Esc·modal 여부는 호출부의 책임이다.
 * 비모달은 role="dialog"를 주되 aria-modal을 주지 않는다.
 * a-modal__*는 기존 폼/footer portal 선택자와의 호환 계약이다.
 */
export function DialogSurface({
  title,
  titleMark,
  titleAside,
  titleId,
  description,
  descriptionId,
  onClose,
  busy = false,
  footer,
  footerRef,
  children,
  className = '',
  ...props
}: DialogSurfaceProps) {
  const generatedTitleId = useId()
  const generatedDescriptionId = useId()
  const headingId = titleId ?? generatedTitleId
  const descId = descriptionId ?? generatedDescriptionId
  const closeGate = useDisabled(busy, t('reason.requestInFlight'))
  return (
    <div
      aria-describedby={description === undefined ? undefined : descId}
      aria-labelledby={headingId}
      {...props}
      className={`a-dialog-surface ${className}`.trim()}
    >
      <div className="a-modal__head">
        <div className="a-modal__heading">
          {titleAside === undefined ? (
            <h2 className="a-modal__title" id={headingId}>
              {titleMark}
              {title}
            </h2>
          ) : (
            <div className="a-modal__titlerow">
              <h2 className="a-modal__title" id={headingId}>
                {titleMark}
                {title}
              </h2>
              {titleAside}
            </div>
          )}
          {description === undefined ? null : (
            <p className="a-modal__desc" id={descId}>
              {description}
            </p>
          )}
        </div>
        <button
          aria-label={t('action.close')}
          className="a-modal__close"
          onClick={() => {
            if (!busy) onClose()
          }}
          type="button"
          {...closeGate.ariaProps}
        >
          {/* 선 그림 닫기 — 크기는 칸에 비례(`overlay.css` `.a-modal__close > .a-icon`) · 접근 이름은 단추의 「닫기」 */}
          <Icon name="close" />
          {closeGate.reasonNode}
        </button>
      </div>
      <div className="a-modal__body">{children}</div>
      <div className="a-modal__foot" ref={footerRef}>
        {footer}
      </div>
    </div>
  )
}
